import { WELD_INTRODUCTORY_CALL_URL } from "@/constants/external-urls/hubspot";
import { useCallback } from "react";

import { PlanName, PurchasablePlan, RecurrenceInterval } from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Tooltip from "@/components/elements/Tooltip";
import { Heading, TextMuted } from "@/components/elements/Typography";
import { ActiveRowTooltipTrigger } from "@/components/modules/ActiveRow";
import { UpgradeNowButton } from "@/components/modules/UpgradeNowButton";
import {
  useCurrentPlan,
  useSubscriptionStatus,
  useWeldPlans,
} from "@/features/subscription";
import cn from "@/helpers/classNames";
import { formatRowCount, formatRowCountCompact } from "@/shared/formatters";
import { formatLimit } from "@/shared/formatters/limit";
import { formatInterval } from "@/shared/formatters/syncFrequency";

import {
  ActiveRowsIcon,
  ConnectorsIcon,
  SyncFrequencyIcon,
  UsersIcon,
} from "@/components/icons/domain-icons";
import { usePriceByLookupKey } from "../../pricing";
import { useLocaleContext } from "../../shared/providers";
import { useBillingPeriodValue } from "../hooks/useBillingPeriodToggle";
import { FormattedPrice } from "./FormattedPrice";

export function PlanUpgradePillars(props: {
  onSelectPlan: (plan: PurchasablePlan) => void;
}) {
  const { name: currentPlanName } = useCurrentPlan();
  const { plans: allWeldPlans } = useWeldPlans();
  const { subscriptionId } = useSubscriptionStatus();
  const currentPlan =
    subscriptionId == null
      ? null
      : allWeldPlans.find((plan) => plan.name === currentPlanName);

  const upgradePlans = allWeldPlans
    .filter((plan) => {
      if (plan.name === currentPlan?.name && !plan.isLegacyPlan) {
        return true;
      }
      if (plan.isLegacyPlan) {
        return false;
      }
      if (currentPlan?.isLegacyPlan) {
        return true;
      }
      if (currentPlan) {
        return (
          plan.limits.minSyncIntervalInMinutes <=
          currentPlan.limits.minSyncIntervalInMinutes
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (a.name === currentPlan?.name) {
        return -1;
      }
      if (b.name === currentPlan?.name) {
        return 1;
      }
      return (
        b.limits.minSyncIntervalInMinutes - a.limits.minSyncIntervalInMinutes
      );
    });

  const onRefChange = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setTimeout(() => {
        node.scrollIntoView({
          behavior: "smooth",
          inline: "center",
        });
      });
    }
  }, []);

  return (
    <div className="flex gap-5 overflow-x-auto py-4">
      {upgradePlans.map((plan, idx) => {
        if (plan.name === PlanName.Enterprise) {
          return (
            <div
              key={plan.name}
              className="flex w-fit shrink-0 first:ml-auto last:mr-auto"
              ref={idx === 1 ? onRefChange : undefined}
            >
              <EnterprisePlanPillar
                plan={plan}
                isCurrentPlan={plan.name === currentPlan?.name}
              />
            </div>
          );
        }
        return (
          <div
            key={plan.name}
            className="flex w-fit shrink-0 first:ml-auto last:mr-auto"
            ref={idx === 1 ? onRefChange : undefined}
          >
            <PlanPillar
              plan={plan}
              isCurrentPlan={plan.name === currentPlan?.name}
              onSelectPlan={() => props.onSelectPlan(plan)}
              {...(plan.name === PlanName.Business
                ? { highlighted: true, badge: "Best value" }
                : {})}
            />
          </div>
        );
      })}
    </div>
  );
}

function PlanPillar(props: {
  plan: PurchasablePlan;
  isCurrentPlan?: boolean;
  highlighted?: boolean;
  badge?: string;
  onSelectPlan: () => void;
}) {
  return (
    <div
      className={cn(
        "relative flex w-64 flex-col gap-6 rounded-sm border p-5 shadow-lg dark:bg-gray-900",
        {
          "outline-2 outline-offset-1 outline-black outline-solid":
            props.highlighted,
        },
      )}
    >
      {props.badge && (
        <div className="absolute top-0 right-1/4 translate-x-1/2 -translate-y-1/2 rounded-xs bg-black px-3 py-1 text-sm text-white shadow-md">
          {props.badge}
        </div>
      )}
      <Heading className="text-xl font-medium">{props.plan.name}</Heading>
      <div className="grow">
        {props.plan.name === PlanName.Enterprise ? (
          <div className="text-center">
            Get a custom plan tailored
            <br />
            to your business
          </div>
        ) : (
          <PlanPriceInfo plan={props.plan} />
        )}
      </div>
      <div className="text-sm">
        <ul className="flex w-fit list-inside flex-col gap-1">
          <li className="flex items-center gap-4">
            <ConnectorsIcon className="size-4" />
            <div>{formatLimit(props.plan.limits.connectors)} connectors</div>
          </li>
          <li className="flex items-center gap-4">
            <UsersIcon className="size-4" />
            <div>
              {formatLimit(props.plan.limits.users)}{" "}
              {props.plan.limits.users === 1 ? "user" : "users"}
            </div>
          </li>
          <li className="flex items-center gap-4">
            <SyncFrequencyIcon className="size-4" />
            {formatInterval(props.plan.limits.minSyncIntervalInMinutes)} sync
            frequency
          </li>
          <li className="flex items-center gap-4">
            <ActiveRowsIcon className="size-4" />
            <div>
              <Tooltip content={formatRowCount(props.plan.limits.rows)}>
                <span>
                  {formatLimit(props.plan.limits.rows, formatRowCountCompact)}{" "}
                  monthly
                </span>
              </Tooltip>{" "}
              <ActiveRowTooltipTrigger>
                <span className="underline decoration-dashed underline-offset-2">
                  active rows
                </span>
              </ActiveRowTooltipTrigger>
            </div>
          </li>
        </ul>
        <div className="mt-2 text-xs text-muted-foreground">
          Upgradable with <span className="font-semibold">add-ons</span>
        </div>
      </div>

      <div className="flex justify-center">
        {props.isCurrentPlan ? (
          <>
            {!props.plan.isEnterprisePlan ? (
              <UpgradeNowButton
                planName={props.plan.name}
                isFullWidth
                source="plans overview"
                onClick={(e) => {
                  e.preventDefault();
                  props.onSelectPlan();
                }}
                size="md"
                variant="outline"
                className="bg-transparent"
              >
                Modify current plan
              </UpgradeNowButton>
            ) : (
              <Button isFullWidth isDisabled className="opacity-50">
                Your current plan
              </Button>
            )}
          </>
        ) : props.plan.name === PlanName.Enterprise ? (
          <Button
            variant="solid"
            colorScheme="primary"
            isFullWidth
            as="a"
            href={WELD_INTRODUCTORY_CALL_URL}
            target="_blank"
            rel="noreferrer"
          >
            Talk to us
          </Button>
        ) : (
          <UpgradeNowButton
            planName={props.plan.name}
            isFullWidth
            source="plans overview"
            onClick={(e) => {
              e.preventDefault();
              props.onSelectPlan();
            }}
            size="md"
          >
            Upgrade to {props.plan.name}
          </UpgradeNowButton>
        )}
      </div>
    </div>
  );
}

function EnterprisePlanPillar(props: {
  plan: PurchasablePlan;
  isCurrentPlan?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex w-72 flex-col gap-6 rounded-sm border p-5 shadow-lg dark:bg-gray-900",
      )}
    >
      <Heading className="text-xl font-medium">{props.plan.name}</Heading>
      <div className="flex grow items-center justify-center text-center">
        Get a custom plan tailored
        <br />
        to your business
      </div>
      <div className="text-sm">
        <ul className="flex w-fit list-inside flex-col gap-1">
          <li className="flex items-center gap-4">
            <ConnectorsIcon className="size-4" />
            Custom # connectors
          </li>
          <li className="flex items-center gap-4">
            <UsersIcon className="size-4" />
            Custom # users
          </li>
          <li className="flex items-center gap-4">
            <SyncFrequencyIcon className="size-4" />
            Custom # sync frequency
          </li>
          <li className="flex items-center gap-4">
            <ActiveRowsIcon className="size-4 shrink-0" />
            <div>
              Custom # monthly{" "}
              <ActiveRowTooltipTrigger>
                <span className="underline decoration-dashed underline-offset-2">
                  active rows
                </span>
              </ActiveRowTooltipTrigger>
            </div>
          </li>
        </ul>
        <div className="mt-2 text-xs text-muted-foreground">&nbsp;</div>
      </div>
      <div className="flex justify-center">
        <Button
          variant="outline"
          colorScheme="primary"
          isFullWidth
          as="a"
          href={WELD_INTRODUCTORY_CALL_URL}
          target="_blank"
          rel="noreferrer"
          size="md"
          className="bg-transparent"
        >
          Talk to us
        </Button>
      </div>
    </div>
  );
}

function PlanPriceInfo(props: { plan: PurchasablePlan }) {
  const billingPeriod = useBillingPeriodValue();

  const { currency } = useLocaleContext();

  const priceLookupKey =
    billingPeriod === RecurrenceInterval.Year
      ? props.plan.priceLookupKeys?.plan.yearly
      : props.plan.priceLookupKeys?.plan.monthly;

  const { price, loading } = usePriceByLookupKey({
    lookupKey: priceLookupKey,
    currency,
  });

  if (loading || !price) {
    return (
      <div className="flex h-14 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-14 flex-col text-sm">
      <div>
        <FormattedPrice price={price} convertBillingInterval="Month">
          <div className="">
            <span className="relative text-3xl font-semibold">
              <FormattedPrice.Price options={{ maximumFractionDigits: 0 }} />
            </span>
          </div>
          <TextMuted as="div" className="text-xs font-normal">
            <FormattedPrice.Period includeBillingInterval />
          </TextMuted>
        </FormattedPrice>
      </div>
    </div>
  );
}
